import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useLanguage, t, API } from './Common';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import LogAnalysis from '@/components/LogAnalysis';

const AssessmentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [deviceType, setDeviceType] = useState(location.state?.deviceType || 'ios');
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [showLogAnalysis, setShowLogAnalysis] = useState(false);
  const [logAnalysisResult, setLogAnalysisResult] = useState(null);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/questions`);
      setQuestions(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching questions:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  const handleAnswer = (answer) => {
    setAnswers({ ...answers, [questions[currentIndex].id]: answer });
    
    if (currentIndex < questions.length - 1) {
      setTimeout(() => setCurrentIndex(currentIndex + 1), 300);
    } else {
      // Last question answered, show log analysis
      setShowLogAnalysis(true);
    }
  };

  const handleLogAnalysisComplete = (result) => {
    setLogAnalysisResult(result);
    submitAssessment();
  };

  const handleSkipLogAnalysis = () => {
    submitAssessment();
  };

  const submitAssessment = async () => {
    const assessmentData = {
      language,
      device_type: deviceType,
      answers: Object.entries(answers).map(([question_id, answer]) => ({
        question_id,
        answer
      }))
    };

    try {
      const response = await axios.post(`${API}/assessments`, assessmentData);
      navigate('/results', { 
        state: { 
          assessment: response.data,
          logAnalysis: logAnalysisResult 
        } 
      });
    } catch (error) {
      console.error('Error submitting assessment:', error);
    }
  };

  const goBack = () => {
    if (showLogAnalysis) {
      setShowLogAnalysis(false);
    } else if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else {
      navigate('/device-selection');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('processing', language)}</p>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-xl text-muted-foreground">No questions available. Please contact administrator.</p>
        </div>
      </div>
    );
  }

  // Show Log Analysis step after all questions
  if (showLogAnalysis) {
    const totalSteps = questions.length + 1; // +1 for log analysis
    const logAnalysisProgress = 100;
    
    return (
      <div className="min-h-screen p-6">
        <div className="scan-line" />
        <LanguageSwitcher />
        <div className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-sm border-b border-white/10 p-4">
          <div className="max-w-2xl mx-auto">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-mono uppercase tracking-widest text-muted-foreground">
                {language === 'it' ? 'Analisi Log' : 'Log Analysis'}
              </span>
              <span className="text-sm font-mono text-primary">{Math.round(logAnalysisProgress)}%</span>
            </div>
            <Progress value={logAnalysisProgress} className="h-1" data-testid="log-analysis-progress" />
          </div>
        </div>

        <div className="max-w-2xl mx-auto pt-24">
          <div className="glass-panel rounded-sm p-8 md:p-12 mb-6">
            <LogAnalysis 
              language={language}
              deviceType={deviceType}
              onComplete={handleLogAnalysisComplete}
              onSkip={handleSkipLogAnalysis}
            />
          </div>
          
          <Button
            variant="outline"
            onClick={goBack}
            data-testid="back-from-log-analysis"
            className="w-full border-white/20 hover:border-primary/50 rounded-none uppercase tracking-widest font-mono"
          >
            {t('back', language)}
          </Button>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;

  const getInstructions = (q) => {
    const deviceKey = deviceType.toLowerCase();
    const langKey = language === 'it' ? '_it' : '_en';
    const instructionKey = `instructions_${deviceKey}${langKey}`;
    return q[instructionKey] || '';
  };

  const getDeviceImages = (q) => {
    const deviceKey = deviceType.toLowerCase();
    const imagesKey = `images_${deviceKey}`;
    return q[imagesKey] || [];
  };

  return (
    <div className="min-h-screen p-6">
      <div className="scan-line" />
      <LanguageSwitcher />
      <div className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-sm border-b border-white/10 p-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-mono uppercase tracking-widest text-muted-foreground">
              {t('question', language)} {currentIndex + 1} {t('of', language)} {questions.length}
            </span>
            <span className="text-sm font-mono text-primary">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-1" data-testid="assessment-progress" />
        </div>
      </div>

      <div className="max-w-2xl mx-auto pt-24">
        <div className="glass-panel rounded-sm p-8 md:p-12 mb-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <h2 className="text-[1.5rem] md:text-3xl font-semibold mb-6 leading-snug" data-testid="current-question-text">
            {language === 'it' ? currentQuestion.question_it : currentQuestion.question_en}
          </h2>

          <div className="mb-6 p-4 bg-muted/20 rounded-sm border border-white/5">
            <p className="text-[1.05rem] md:text-base text-muted-foreground leading-relaxed">
              {language === 'it' ? currentQuestion.explanation_it : currentQuestion.explanation_en}
            </p>
          </div>

          {getInstructions(currentQuestion) && (
            <Accordion type="single" collapsible className="mb-8">
              <AccordionItem value="instructions" className="border border-white/10 rounded-sm">
                <AccordionTrigger 
                  className="px-4 hover:no-underline hover:bg-white/5"
                  data-testid="show-instructions-button"
                >
                  <span className="font-mono uppercase tracking-widest text-sm">
                    {t('showInstructions', language)}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <div className="prose prose-invert prose-sm max-w-none">
                    <p className="text-[1.05rem] md:text-base text-muted-foreground whitespace-pre-wrap mb-4 leading-relaxed">
                      {getInstructions(currentQuestion)}
                    </p>
                    
                    {/* Show images for selected device only */}
                    {getDeviceImages(currentQuestion).length > 0 && (
                      <div className="space-y-3 mt-4">
                        <p className="text-sm font-semibold text-primary">
                          {language === 'it' ? `📱 Screenshot per ${deviceType.toUpperCase()}:` : `📱 Screenshots for ${deviceType.toUpperCase()}:`}
                        </p>
                        <div className="grid gap-3">
                          {getDeviceImages(currentQuestion).map((img, idx) => (
                            <div key={idx} className="border border-white/10 rounded-sm overflow-hidden">
                              <img 
                                src={img} 
                                alt={`${deviceType} instruction ${idx + 1}`}
                                className="w-full h-auto"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}

          <div className="grid grid-cols-2 gap-4 mb-6">
            <Button
              onClick={() => handleAnswer(true)}
              data-testid="answer-yes-button"
              className="h-16 rounded-none border border-secondary bg-secondary/10 text-secondary hover:bg-secondary hover:text-white transition-all duration-300 uppercase tracking-widest font-mono text-lg"
            >
              {t('yes', language)}
            </Button>
            <Button
              onClick={() => handleAnswer(false)}
              data-testid="answer-no-button"
              className="h-16 rounded-none border border-primary bg-primary/10 text-primary hover:bg-primary hover:text-black transition-all duration-300 uppercase tracking-widest font-mono text-lg"
            >
              {t('no', language)}
            </Button>
          </div>

          <Button
            variant="outline"
            onClick={goBack}
            data-testid="back-button"
            className="w-full border-white/20 hover:border-primary/50 rounded-none uppercase tracking-widest font-mono"
          >
            {t('back', language)}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AssessmentPage;
